import { Component, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import {
    GetCurrentUser200Response,
    NewsLetterRequest,
    NotificationsService,
    SubscribeToNewsLetters201Response,
    UsersService,
} from '@viewbid/ts-angular-client';
import { ReCaptchaV3Service } from 'ng-recaptcha-2';
import { Observable, Subject, take, takeUntil } from 'rxjs';
import { User } from 'viewbid-shared-lib';
import { selectUser } from 'src/app/viewbid/state/app-selectors';
import { AppState } from 'src/app/viewbid/state/app-state';
import { NewsletterSubscriptionCacheService } from 'src/app/viewbid/shared/services/newsletter-subscription-cache.service';

/**
 * Newsletter subscription status for the signed-in user — hosts the
 * `vb-newsletter-card` redesign component, which replaces the legacy
 * email-input signup form (the component name and selector are kept so the
 * home and contact-us host pages stay untouched). Reads the user's email and
 * subscription status, and gates subscribe/unsubscribe behind a vb-dialog
 * confirm — same recipe as the remove-address confirm in user-addresses.
 */
@Component({
    selector: 'vb-newsletter-subscription-form',
    templateUrl: './newsletter-subscription-form.component.html',
    styleUrls: ['./newsletter-subscription-form.component.scss'],
})
export class NewsletterSubscriptionFormComponent implements OnInit, OnDestroy {
    public email: string = '';
    public subscribed: boolean = false;
    public pending: boolean = false;
    public error: string | null = null;

    /** Card renders once email + subscription status are known. */
    public loaded: boolean = false;

    /** Email could not be resolved (API failure) — stop the spinner rather
     *  than leave it running forever; the section renders nothing. */
    public loadFailed: boolean = false;

    /** Subscribe/unsubscribe confirm (vb-dialog). */
    public confirmOpen: boolean = false;
    public requestedState: boolean | null = null;

    private destroy$ = new Subject<void>();

    constructor(
        private store: Store<AppState>,
        private usersService: UsersService,
        private notificationsService: NotificationsService,
        private translate: TranslateService,
        private recaptchaV3Service: ReCaptchaV3Service,
        private newsletterCache: NewsletterSubscriptionCacheService,
    ) {}

    ngOnInit(): void {
        // Fast path: the auth store usually already holds the signed-in user,
        // so a synchronous snapshot skips the getCurrentUser round trip. The
        // store isn't guaranteed to be hydrated on every navigation though —
        // its UserStateChangeAction comes from auth events, not route changes
        // — so an empty snapshot falls back to the API instead of waiting on
        // an emission that may never come.
        this.store
            .select(selectUser)
            .pipe(take(1), takeUntil(this.destroy$))
            .subscribe((user: User | null) => {
                if (user?.email) {
                    this.setEmail(user.email);
                } else {
                    this.fetchEmailFromApi();
                }
            });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private fetchEmailFromApi(): void {
        this.usersService
            .getCurrentUser()
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response: GetCurrentUser200Response) => {
                    const email: string = response.data?.email?.email ?? '';
                    if (email) {
                        this.setEmail(email);
                    } else {
                        this.loadFailed = true;
                    }
                },
                error: () => {
                    this.loadFailed = true;
                },
            });
    }

    private setEmail(email: string): void {
        this.email = email;

        // fallback to cached value while we wait for proper fetch
        this.subscribed = this.newsletterCache.getCached(email) ?? true;
        this.loaded = true;

        this.fetchSubscriptionStatus();
    }

    private fetchSubscriptionStatus(): void {
        this.notificationsService
            .getNewsletterSubscriptionByEmail(this.email)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response: SubscribeToNewsLetters201Response) => {
                    const subscribed: boolean =
                        response.data?.isSubscribed ?? false;
                    this.subscribed = subscribed;
                    this.loaded = true;
                    this.newsletterCache.set(this.email, subscribed);
                },
                // No subscription record reads as "not subscribed".
                error: () => {
                    this.subscribed = false;
                    this.loaded = true;
                    this.newsletterCache.set(this.email, false);
                },
            });
    }

    onToggleRequested(nextState: boolean): void {
        this.requestedState = nextState;
        this.confirmOpen = true;
    }

    get confirmTitle(): string {
        return this.translate.instant(
            this.requestedState
                ? 'NewsLetters.SubscribeConfirmationTitle'
                : 'NewsLetters.UnsubscribeConfirmationTitle',
        );
    }

    get confirmMessage(): string {
        return this.translate.instant(
            this.requestedState
                ? 'NewsLetters.SubscribeConfirmationMessage'
                : 'NewsLetters.UnsubscribeConfirmationMessage',
        );
    }

    get confirmActionLabel(): string {
        return this.requestedState ? 'Subscribe' : 'Unsubscribe';
    }

    closeConfirm(): void {
        this.confirmOpen = false;
        this.requestedState = null;
    }

    onConfirmed(): void {
        const nextState: boolean | null = this.requestedState;
        this.closeConfirm();
        if (nextState !== null) {
            this.applySubscriptionChange(nextState);
        }
    }

    private applySubscriptionChange(nextState: boolean): void {
        this.pending = true;
        this.error = null;
        this.recaptchaV3Service
            .execute('submit')
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                error: () => {
                    this.pending = false;
                    this.error = this.translate.instant(
                        nextState
                            ? 'NewsLetters.SubscribeFailed'
                            : 'NewsLetters.UnsubscribeFailed',
                    );
                },
                next: (token: string) => {
                    const newsLettersObject: NewsLetterRequest = {
                        email: this.email,
                        reCaptchaToken: token,
                    };
                    const request$: Observable<unknown> = nextState
                        ? this.notificationsService.subscribeToNewsLetters(
                              newsLettersObject,
                          )
                        : this.notificationsService.unsubscribeNewsletters(
                              this.email,
                              token,
                          );

                    request$.pipe(takeUntil(this.destroy$)).subscribe({
                        next: () => {
                            this.pending = false;
                            // Flipping `subscribed` is what tells the card the
                            // request succeeded — it shows its SAVED strip.
                            this.subscribed = nextState;
                            this.newsletterCache.set(this.email, nextState);
                        },
                        error: (error: ErrorEvent) => {
                            this.pending = false;
                            // 10090 carries a user-facing message (e.g. already
                            // subscribed) — same handling as the legacy form.
                            this.error =
                                error.error?.errorCode === 10090
                                    ? error.error.message
                                    : this.translate.instant(
                                          nextState
                                              ? 'NewsLetters.SubscribeFailed'
                                              : 'NewsLetters.UnsubscribeFailed',
                                      );
                        },
                    });
                },
            });
    }
}
